import { config } from "@app-contexts/sdk/services/config";
import {
  Widget,
  type CalendarWidgetParams,
  type WidgetLaunchOptions,
} from "@app-contexts/sdk/services/Widget";

// handlers are dependent on each other - checkout listener needs calendar
// and calendar needs checkout
// eslint-disable-next-line import/no-cycle
import {
  checkoutWidget,
  eventPurchaseWidget,
  expressCheckoutWidget,
  prepareWidgetParams,
} from "./checkout";
import {
  getCustomerAttributionWidgetOptions,
  getEmbeddedWidgetCallArgs,
  getEmbeddedWidgetOptions,
} from "./embedded-widget-options";
import { createEmbeddedWidgetTransition } from "./embedded-widget-transition";
import { inheritCustomerAttributionToken } from "./customer-attribution";

/**
 * Opens the calendar widget
 * @param venueId id of venue
 * @param params values to add to URL params (e.g. 'theme-primary', 'theme-secondary', etc.)
 * @param containerId id of the container to embed the widget in
 * @param options optional host callback used to refresh a rejected attribution token
 */
export async function calendarWidget(
  venueId: string | number,
  params: CalendarWidgetParams = {},
  containerId?: string,
  options: WidgetLaunchOptions = {}
) {
  const {
    calendarDate,
    calendarTimeslot,
    calendarSpecialEvent,
    displayPricePatronTypeId,
    hidePrice,
    highlightMultiDayEventsOnStartDateOnly,
    ...widgetParams
  } = params;
  const isAttraction = widgetParams.is_attraction === true;
  const enrichedParams = prepareWidgetParams({
    ...widgetParams,
    ...(calendarDate !== undefined && { calendar_date: calendarDate }),
    ...(calendarTimeslot !== undefined && {
      calendar_timeslot: calendarTimeslot,
    }),
    ...(calendarSpecialEvent !== undefined && {
      calendar_special_event: calendarSpecialEvent,
    }),
    ...(displayPricePatronTypeId !== undefined && {
      display_price_patron_type_id: String(displayPricePatronTypeId),
    }),
    ...(hidePrice !== undefined && { hide_price: hidePrice }),
    ...(highlightMultiDayEventsOnStartDateOnly !== undefined && {
      highlight_multi_day_events_on_start_date_only:
        highlightMultiDayEventsOnStartDateOnly,
    }),
  });

  const url = await config.calendarWidgetUrl(
    venueId,
    isAttraction,
    enrichedParams?.event_id
  );
  const widget = new Widget({
    src: url,
    containerId,
    queryParams: enrichedParams,
    ...getEmbeddedWidgetOptions(containerId),
    ...getCustomerAttributionWidgetOptions(enrichedParams, options),
  });
  const replaceEmbeddedWidget = createEmbeddedWidgetTransition(widget);

  widget.show();

  widget
    .on("showpass.calendar.event-purchase", ({ slug, params }) => {
      const eventParams = inheritCustomerAttributionToken(
        enrichedParams,
        params
      );

      if (containerId) {
        replaceEmbeddedWidget(() =>
          eventPurchaseWidget(
            slug,
            eventParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        eventPurchaseWidget(
          slug,
          eventParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    })
    .on("showpass.calendar.checkout", () => {
      if (containerId) {
        replaceEmbeddedWidget(() =>
          checkoutWidget(
            enrichedParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        widget.close({ removeBodyClass: false, remount: false });
        checkoutWidget(
          enrichedParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    })
    .on("showpass.event-purchase.express-checkout", () => {
      if (containerId) {
        replaceEmbeddedWidget(() =>
          expressCheckoutWidget(
            enrichedParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        widget.close({ removeBodyClass: false, remount: false });
        expressCheckoutWidget(
          enrichedParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    })
    .on("showpass.calendar.remount", () => {
      widget.remount();
    });

  return widget;
}

/**
 *  @deprecated use calendarWidget with the "containerId" query param set instead
 *  This function is only used in the v1 SDK
 *  @param options optional host callback used to refresh a rejected attribution token
 */
export function mountCalendarWidget(
  id: string | number,
  params: CalendarWidgetParams,
  options: WidgetLaunchOptions = {}
) {
  return calendarWidget(id, params, "showpass-calendar-widget", options);
}
